import { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { getCodingData, CodingVisit } from '../services/rogerDocApi';
import { areStatesEqual } from '../services/diagnosisStateService';
import type {
  CodingDataResponse,
  CaseMixData,
} from '../types/codingData.types';
import type {
  DiagnosisStateData,
  SavedDiagnosisState,
} from '../types/diagnosisState.types';

export const ORIGINAL_STATE_NAME = 'Original AI codes by Roger';

/**
 * The coding page judges codes on the response's top-level `codes_as_of`,
 * the coding as-of date (M0090, or the visit date) Roger Docs sends on every
 * ready payload (ROG-3546, EMR-2531).
 * A ready payload without it breaks that contract, so it is refused as a load
 * failure rather than coded against a guessed date.
 */
export function requireCodesAsOf(data: CodingDataResponse): CodingDataResponse {
  const codesAsOf = data.codes_as_of;
  if (typeof codesAsOf !== 'string' || codesAsOf.trim() === '') {
    throw new Error('Coding data has no codes_as_of');
  }
  return data;
}

export const useCoding = () => {
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );
  const { diagnosisId: urlDiagnosisId, stateId: urlStateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Core data state
  const [selectedVisit, setSelectedVisit] = useState<CodingVisit | null>(null);
  const [loading, setLoading] = useState(false);
  const [responseData, setResponseData] = useState<CodingDataResponse | null>(
    null
  );

  // Polling state
  const [isPolling, setIsPolling] = useState(false);
  const [processingStatus, setProcessingStatus] = useState<string | null>(null);

  // Diagnosis state management
  const [lastSavedState, setLastSavedState] = useState<DiagnosisStateData>();
  const [activeStateId, setActiveStateId] = useState<string | null>(null);
  const [originalRootData, setOriginalRootData] =
    useState<DiagnosisStateData>();
  const [originalCaseMix2ndPeriod, setOriginalCaseMix2ndPeriod] = useState<
    CaseMixData | undefined
  >();

  const loadingDiagnosisIdRef = useRef<string | null>(null);

  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollInterval = 20000; // 20 seconds

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, []);

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsPolling(false);
  };

  // Helper to get diagnosis ID (works with both manual selection and URL navigation)
  const getDiagnosisId = (): string | null => {
    return (
      selectedVisit?.diagnosis_id || urlDiagnosisId || responseData?.id || null
    );
  };

  // Helper to create DiagnosisStateData from saved state or coding data
  const createStateData = (
    state: SavedDiagnosisState | CodingDataResponse
  ): DiagnosisStateData => {
    return {
      primary_diagnosis: state.primary_diagnosis,
      secondary_diagnoses: state.secondary_diagnoses,
      case_mix_data: state.case_mix_data,
    };
  };

  // Helper to update response data with new state
  const updateResponseDataWithState = (
    data: CodingDataResponse,
    stateData: DiagnosisStateData,
    source: Pick<
      SavedDiagnosisState | CodingDataResponse,
      'case_mix_data_2nd_period'
    >
  ) => {
    setResponseData({
      ...data,
      primary_diagnosis: stateData.primary_diagnosis,
      secondary_diagnoses: stateData.secondary_diagnoses,
      case_mix_data: stateData.case_mix_data,
      case_mix_data_2nd_period: source.case_mix_data_2nd_period,
    });
  };

  const setOriginalRootFromCodingData = (data: CodingDataResponse) => {
    setOriginalRootData(createStateData(data));
    setOriginalCaseMix2ndPeriod(data.case_mix_data_2nd_period);
  };

  const refreshData = async (data: CodingDataResponse) => {
    // Store the original root data (never changes except on refresh)
    setOriginalRootFromCodingData(data);
    const rootData = createStateData(data);

    // Determine which state to use as the base (last saved state or original)
    const hasStates = data.states && data.states.length > 0;
    let baseState: DiagnosisStateData;
    let stateId: string;
    let source: Pick<SavedDiagnosisState, 'case_mix_data_2nd_period'>;

    if (hasStates) {
      // If there are saved states, use the most recent one
      const latestState = data.states![data.states!.length - 1];
      baseState = createStateData(latestState);
      stateId = latestState.id;
      source = latestState;
    } else {
      // If no saved states, use the original data
      baseState = rootData;
      stateId = 'original';
      source = data;
    }

    // Set lastSavedState to the base state
    setLastSavedState(baseState);

    // Set currentState (responseData) to match the base state initially
    updateResponseDataWithState(data, baseState, source);

    setActiveStateId(stateId);
  };

  const startPolling = (diagnosisId: string) => {
    // Stop any existing polling
    stopPolling();

    setIsPolling(true);
    setProcessingStatus('processing');

    // Check status function
    const checkStatus = async () => {
      try {
        const data = await getCodingData(diagnosisId);

        if (
          data &&
          typeof data === 'object' &&
          'status' in data &&
          data.status === 'ready'
        ) {
          stopPolling();
          await refreshData(requireCodesAsOf(data as CodingDataResponse));
          setProcessingStatus(null);
          showSuccess('Data fetched successfully');
        } else if (
          data &&
          typeof data === 'object' &&
          'status' in data &&
          data.status === 'in_progress'
        ) {
          // Continue polling - status remains 'processing'
          setProcessingStatus('processing');
        } else if (
          data &&
          typeof data === 'object' &&
          'status' in data &&
          data.status === 'not_processing'
        ) {
          stopPolling();
          setProcessingStatus('not_processing');
          setResponseData(null);
        }
      } catch (error: unknown) {
        console.error('Error fetching data:', error);
        stopPolling();
        setProcessingStatus('error');
        showError('Failed to fetch coding data. Please try again.');
      }
    };

    // Start interval polling
    pollIntervalRef.current = setInterval(checkStatus, pollInterval);
  };

  const loadDataFromUrl = async (diagnosisId: string, stateId?: string) => {
    if (!diagnosisId?.trim()) {
      showError('Invalid diagnosis ID in URL');
      navigate('/coding', { state: location.state });
      return;
    }

    // Prevent duplicate fetch for the same ID
    if (loadingDiagnosisIdRef.current === diagnosisId) {
      return;
    }
    loadingDiagnosisIdRef.current = diagnosisId;

    // Reset all states
    setLoading(true);
    setResponseData(null);
    setProcessingStatus(null);
    stopPolling();

    try {
      const data = await getCodingData(diagnosisId);

      if (
        data &&
        typeof data === 'object' &&
        'status' in data &&
        data.status === 'ready'
      ) {
        const codingData = requireCodesAsOf(data as CodingDataResponse);

        // If stateId is provided in URL, try to find and load that specific state
        // We do this BEFORE refreshData so we can override the default behavior
        if (stateId && codingData.states && codingData.states.length > 0) {
          const targetState = codingData.states.find(s => s.id === stateId);

          if (targetState) {
            // Load this specific state instead of the default (most recent) state
            const stateData = createStateData(targetState);

            // Store the original root data
            setOriginalRootFromCodingData(codingData);

            // Set the target state as both last saved and current
            setLastSavedState(stateData);
            updateResponseDataWithState(codingData, stateData, targetState);
            setActiveStateId(stateId);
            showSuccess('Version loaded successfully');
          } else {
            // State ID in URL not found, load original state and show warning
            await refreshData(codingData);
            showError('Version not found, showing original version');
            navigate(`/coding/${diagnosisId}`, { state: location.state });
          }
        } else {
          // No stateId in URL, use default refreshData behavior
          await refreshData(codingData);

          // If there are saved states, redirect to the latest one
          if (codingData.states && codingData.states.length > 0) {
            const latestState = codingData.states[codingData.states.length - 1];
            navigate(`/coding/${diagnosisId}/states/${latestState.id}`, {
              replace: true,
              state: location.state,
            });
          }

          showSuccess('Data fetched successfully');
        }
      } else if (
        data &&
        typeof data === 'object' &&
        'status' in data &&
        data.status === 'in_progress'
      ) {
        // Start polling - it will set isPolling, processingStatus, and pollAttempts
        startPolling(diagnosisId);
      } else if (
        data &&
        typeof data === 'object' &&
        'status' in data &&
        data.status === 'not_processing'
      ) {
        setProcessingStatus('not_processing');
      }
    } catch (error: unknown) {
      console.error('Error fetching data from URL:', error);
      setProcessingStatus('error');
      showError('Failed to load coding data. Please try again.');
      // Navigate back to search on error
      navigate('/coding', { state: location.state });
    } finally {
      setLoading(false);
      loadingDiagnosisIdRef.current = null;
    }
  };

  const handleSelectState = (stateId: string) => {
    const state = getAllStates().find(s => s.id === stateId);
    if (state && responseData) {
      // Update both lastSavedState and currentState (responseData) to match selected state
      const newStateData = createStateData(state);

      setLastSavedState(newStateData);
      updateResponseDataWithState(responseData, newStateData, state);
      setActiveStateId(stateId);
      showSuccess('Version loaded successfully');

      // Update URL to match selection
      // Use selectedVisit.diagnosis_id or urlDiagnosisId as fallback
      const diagId =
        selectedVisit?.diagnosis_id || urlDiagnosisId || responseData.id;
      if (state.isOriginal) {
        navigate(`/coding/${diagId}`, { state: location.state });
      } else {
        navigate(`/coding/${diagId}/states/${state.id}`, {
          state: location.state,
        });
      }
    }
  };

  const handleStateUpdated = (updatedState: DiagnosisStateData) => {
    // Called after successful state update (PUT) - update lastSavedState without API call
    setLastSavedState(updatedState);

    // Keep responseData.states in sync so switching away and back uses the latest save
    setResponseData(prev => {
      if (!prev?.states || !activeStateId || activeStateId === 'original') {
        return prev;
      }

      return {
        ...prev,
        states: prev.states.map(state =>
          state.id === activeStateId
            ? {
                ...state,
                primary_diagnosis: updatedState.primary_diagnosis,
                secondary_diagnoses: updatedState.secondary_diagnoses,
                case_mix_data: updatedState.case_mix_data,
                case_mix_data_2nd_period: prev.case_mix_data_2nd_period,
              }
            : state
        ),
      };
    });
  };

  const handleRefresh = useCallback(async () => {
    // After save/delete, refetch data to get updated states and reset both states
    const diagnosisId = getDiagnosisId();
    if (!diagnosisId) return;

    try {
      const data = await getCodingData(diagnosisId);
      if (
        data &&
        typeof data === 'object' &&
        'status' in data &&
        data.status === 'ready'
      ) {
        await refreshData(requireCodesAsOf(data as CodingDataResponse));
        showSuccess('Versions updated successfully');
      }
    } catch (error) {
      console.error('Error refreshing states:', error);
      showError('Failed to refresh versions');
    }
  }, [selectedVisit, urlDiagnosisId, responseData, showSuccess, showError]);

  const getAllStates = (): SavedDiagnosisState[] => {
    if (!responseData || !originalRootData) return [];

    // Get diagnosis ID from selectedVisit, URL, or responseData
    const diagId =
      selectedVisit?.diagnosis_id || urlDiagnosisId || responseData.id;

    // Create the Original State from the root data (not from current responseData)
    // This ensures "Original State" always reflects the unmodified root object
    const originalState: SavedDiagnosisState = {
      id: 'original',
      diagnosis_id: diagId,
      name: ORIGINAL_STATE_NAME,
      created_by: 'System',
      created_at: new Date().toISOString(),
      isOriginal: true,
      primary_diagnosis: originalRootData.primary_diagnosis,
      secondary_diagnoses: originalRootData.secondary_diagnoses,
      case_mix_data: originalRootData.case_mix_data,
      case_mix_data_2nd_period: originalCaseMix2ndPeriod,
    };

    // Combine Original State with saved states
    const savedStates =
      (
        responseData as CodingDataResponse & {
          states?: SavedDiagnosisState[];
        }
      )?.states || [];

    return [originalState, ...savedStates];
  };

  // URL-based loading and state sync effect
  useEffect(() => {
    if (!urlDiagnosisId) return;

    const urlIndicatesStateId = urlStateId || 'original';
    const isDifferentDiagnosis =
      responseData?.id && responseData.id !== urlDiagnosisId;
    const isDifferentState = activeStateId !== urlIndicatesStateId;
    const hasNoData = !responseData;

    // Load new diagnosis data if: no data yet OR different diagnosis
    if ((hasNoData || isDifferentDiagnosis) && !loading) {
      loadDataFromUrl(urlDiagnosisId, urlStateId);
      return;
    }

    // Otherwise, if same diagnosis but different state, just switch state locally (no API call)
    if (isDifferentState && responseData && originalRootData) {
      const allStates = getAllStates();
      const targetState = allStates.find(s => s.id === urlIndicatesStateId);

      if (targetState) {
        const newStateData = createStateData(targetState);
        setLastSavedState(newStateData);
        updateResponseDataWithState(responseData, newStateData, targetState);
        setActiveStateId(targetState.id);
      } else if (urlIndicatesStateId !== 'original' && !loading) {
        // State not found in current list - reload data to get updated states
        // This handles the case when a new state was just created and URL was updated
        loadDataFromUrl(urlDiagnosisId, urlStateId);
      }
    }
  }, [urlDiagnosisId, urlStateId]);

  return {
    // Data (read)
    responseData,
    loading,
    selectedVisit,
    lastSavedState,
    activeStateId,
    originalRootData,

    // Data (write) - expose setters for component editing
    setResponseData,
    setSelectedVisit,
    setLastSavedState,

    // State management
    getAllStates,
    handleSelectState,
    handleStateUpdated,
    handleRefresh,

    // Polling
    isPolling,
    processingStatus,

    // Helpers
    getDiagnosisId,

    // URL params
    urlDiagnosisId,
    urlStateId,

    // Shared state
    hasUnsavedChanges:
      !!responseData &&
      !!lastSavedState &&
      !areStatesEqual(
        {
          primary_diagnosis: responseData.primary_diagnosis,
          secondary_diagnoses: responseData.secondary_diagnoses,
          case_mix_data: responseData.case_mix_data,
        },
        lastSavedState
      ),
  };
};
